/**
 * RootRoute
 *
 * Element for the root path "/". Decides what to show based on the host:
 *  - Apex marketing domain (nexedu.ma / www) and dev hosts → public HomePage.
 *  - Any tenant/reserved subdomain (e.g. immt.nexedu.ma) → never the marketing
 *    home: send authenticated users to their dashboard, everyone else to the
 *    tenant login page.
 */
import { Navigate } from 'react-router-dom';
import { HomePage } from '@/features/marketing/pages/HomePage';
import { useAuth } from '../hooks/useAuth';

// Hosts that serve the platform itself (marketing landing), not a tenant.
const isApexHost = (): boolean => {
  const host = window.location.hostname;
  const baseDomain = (import.meta.env.VITE_BASE_DOMAIN as string) || 'nexedu.ma';
  return (
    host === baseDomain ||
    host === `www.${baseDomain}` ||
    host === 'localhost' ||
    host.startsWith('127.0.0.1') ||
    host.endsWith('.pages.dev') // Cloudflare Pages preview builds
  );
};

export const RootRoute: React.FC = () => {
  const { isAuthenticated, getDashboardPath } = useAuth();

  // Apex / dev hosts: public landing page.
  if (isApexHost()) {
    return <HomePage />;
  }

  // Tenant subdomains: logged-in users go to their dashboard, otherwise login.
  if (isAuthenticated) {
    return <Navigate to={getDashboardPath()} replace />;
  }
  return <Navigate to="/login" replace />;
};

export default RootRoute;
